<script setup>
import { ref, watch, computed } from "vue";
import { useI18n } from "vue-i18n";
import { clickOutSide as vClickOutSide } from "@mahdikhashan/vue3-click-outside";
import { useRoute } from "vue-router";
import { useDownloadsStore } from "@/stores/DownloadsStore";
import { GetDownloadsByStatus } from "@/services/downloads";
import Button from "primevue/button";
import Skeleton from "primevue/skeleton";

const { t } = useI18n();
const route = useRoute();
const downloadsStore = useDownloadsStore();

const tabs = computed(() => [
  {
    key: "processing",
    label: t("process"),
    icon: "pi pi-spinner",
    badge: downloadsStore.processing || null,
    badgeClass: "bg-blue-50 text-blue-600",
  },
  {
    key: "done",
    label: t("report_done"),
    icon: "pi pi-check-circle",
    badge: downloadsStore.done || null,
    badgeClass: "bg-green-50 text-green-600",
  },
  {
    key: "viewed",
    label: t("viewed"),
    icon: "pi pi-eye",
  },
]);

const open = ref(false);
const activeKey = ref("processing");
const items = ref([]);
const loading = ref(false);

const totalBadge = computed(() => downloadsStore.processing + downloadsStore.done);

const hide = () => {
  open.value = false;
};

const loadItems = async (status) => {
  loading.value = true;
  try {
    const response = await GetDownloadsByStatus(status);
    items.value = response?.data ?? [];
  } catch {
    items.value = [];
  } finally {
    loading.value = false;
  }
};

const refreshPanel = async () => {
  await Promise.allSettled([loadItems(activeKey.value), downloadsStore.fetchCount()]);
};

const selectTab = (key) => {
  if (activeKey.value === key) return;
  activeKey.value = key;
  loadItems(key);
};

watch(() => route.path, hide);

watch(open, (val) => {
  if (!val) return;
  refreshPanel();
});
</script>

<template>
  <li
    v-click-out-side="hide"
    class="page-header__nav-item relative flex items-center justify-center"
  >
    <Button
      class="relative p-2 w-10 h-10 text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center rounded-full"
      text
      @click.prevent="open = !open"
    >
      <i class="pi pi-cloud-download text-xl"></i>
      <span
        v-if="totalBadge > 0"
        class="absolute top-1 right-1 w-4 h-4 bg-blue-500 shadow-sm shadow-blue-500/50 rounded-full flex items-center justify-center ring-2 ring-white"
      >
        <span class="text-[.55rem] text-white leading-none font-bold">
          {{ totalBadge > 99 ? "99+" : totalBadge }}
        </span>
      </span>
    </Button>

    <div
      :class="open ? 'page-header__dropdown--show' : ''"
      class="page-header__dropdown mt-2 origin-top-right transition-all duration-200 w-fit"
    >
      <div
        class="header-dropdown shadow-xl shadow-gray-200/50 border border-gray-100 rounded-2xl overflow-hidden bg-white px-2"
      >
        <div class="px-5 pt-5 pb-3">
          <div class="flex items-center gap-2 mb-3">
            <div
              class="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-500"
            >
              <i class="pi pi-download text-sm"></i>
            </div>
            <h4 class="text-base font-bold text-gray-900 m-0 leading-none">
              {{ $t("downloads") }}
            </h4>
          </div>
        </div>

        <div
          class="flex items-center gap-1.5 p-1.5 bg-gray-50 rounded-xl border border-gray-200/60 w-max"
        >
          <button
            v-for="tab in tabs"
            :key="tab.key"
            type="button"
            class="relative flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-lg transition-all duration-200 border-none outline-none focus:outline-none appearance-none select-none"
            :class="
              activeKey === tab.key
                ? 'bg-white text-gray-900 shadow-sm ring-1 ring-black/5'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100/80'
            "
            @click.prevent.stop="selectTab(tab.key)"
          >
            <i
              v-if="tab.icon"
              :class="[tab.icon, activeKey === tab.key ? 'text-blue-600' : 'text-gray-400']"
              class="text-[14px] transition-colors"
            />

            <span>{{ tab.label }}</span>

            <span
              v-if="tab.badge"
              class="flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-md text-[10px] leading-none font-black ml-0.5 transition-all"
              :class="
                activeKey === tab.key
                  ? 'bg-blue-50 text-blue-600 ring-1 ring-inset ring-blue-100'
                  : 'bg-gray-200 text-gray-500'
              "
            >
              {{ tab.badge }}
            </span>
          </button>
        </div>
        <div v-if="loading" class="px-4 py-4 flex flex-col gap-2">
          <Skeleton v-for="n in 3" :key="n" height="2.5rem" />
        </div>

        <template v-else-if="items.length">
          <div class="max-h-[360px] overflow-y-auto no-scrollbar pb-2">
            <div
              v-for="item in items"
              :key="item.uuid"
              class="px-5 py-3 mx-2 my-1 rounded-xl transition-colors hover:bg-gray-50 flex items-center justify-between gap-3 group"
            >
              <div class="min-w-0 flex-1">
                <p class="text-xs font-medium text-gray-800 truncate" :title="item.title">
                  {{ item.title }}
                </p>
                <div class="flex items-center gap-2 text-[10px] text-gray-400 mt-1">
                  <span><i class="pi pi-clock text-[9px] mr-1"></i>{{ item.queued_at }}</span>
                  <span
                    v-if="item.storage_file?.size"
                    class="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-medium"
                    >{{ item.storage_file.size }}</span
                  >
                </div>
              </div>
              <a
                v-if="item.storage_file?.path"
                :href="item.storage_file.path"
                :download="item.storage_file.name"
                target="_blank"
                rel="noopener"
                class="w-8 h-8 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center shrink-0 shadow-sm"
              >
                <i class="pi pi-download text-sm" />
              </a>
              <div
                v-else
                class="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center shrink-0"
              >
                <i class="pi pi-spin pi-spinner text-blue-500 text-sm" />
              </div>
            </div>
          </div>
        </template>

        <div v-else class="px-6 py-4 text-sm text-gray-400 text-center">
          {{ $t("no_data") }}
        </div>
      </div>
    </div>
  </li>
</template>
